import _isNil from 'lodash-es/isNil';
import _omitBy from 'lodash-es/omitBy';

import {
  asTypedAsset,
  putAssetRequest,
} from '@iconik-io/iconik-api/src/schema_patched/assets';
import {
  type AssetsAssetSchemaTypedPartial,
  type AssetsAssetSchemaTypedWithId,
  type AssetsBulkDeleteSchema,
  type AssetsBulkSetApprovalSchema,
  httpPatchAssetsByAssetId,
  httpPostApprovalsBulk,
  httpPostApprovalsBulkRemove,
  httpPostAssetsByAssetIdReindex,
  httpPostAssetsByAssetIdSegmentsReindex,
  httpPostDeleteQueueAssets,
  httpPostDeleteQueueBulk,
} from '@iconik-io/iconik-api/src/services/assets';
import { httpPostFormatsByFormatIdStoragesByStorageId } from '@iconik-io/iconik-api/src/services/files';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { createEntityDenormalizer } from '@iconik-io/shared-lib/src/redux/entities/normalizers';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import { ASSET_PAGE_ROUTE } from '../../../constants/routes';
import type { AppDispatch, AppState, GetState } from '../../../store/types';
import { getChangedFields } from '../../../utils/object_type';
import { segmentTrackEvent } from '../../analytics_tracking/thunks';
import {
  historyGoBack,
  historyPush,
  historyReplace,
} from '../../history/actions';
import { errorMessage, successMessage } from '../../messages/thunks';
import { setAsset } from './slice';

export type RedirectLocation = 'SEARCH' | 'ROOT' | 'NONE';

// When deleting subclip assets we need to update their corresponding segment by
// resetting subclip_id so that we don't think there is a subclip for that segment
const getSegmentUpdateAfterDelete = (assetIds: string[], state: AppState) => {
  const idsToUpdate = assetIds
    .map((id) => {
      const asset = state.entities.assets[id];
      const searchItem = state.entities.search_items[id];
      if (
        asset?.type === 'SUBCLIP' &&
        asset?.original_segment_id !== undefined
      ) {
        return asset.original_segment_id;
      }
      if (
        searchItem?.type === 'SUBCLIP' &&
        searchItem?.original_segment_id !== undefined
      ) {
        return searchItem.original_segment_id;
      }
      return undefined;
    })
    .filter((segmentId): segmentId is string => segmentId !== undefined);

  return Object.fromEntries(
    idsToUpdate.map((id) => [
      id,
      // biome-ignore lint/plugin: clearing a subclip writes a null the normalized segment has no slot for.
      {
        subclip_id: null,
      } as unknown as Partial<SegmentEntityWithIdNormalized>,
    ]),
  );
};

export function deleteAssetsByIds(ids: string[], redirect?: RedirectLocation) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    dispatch(httpPostDeleteQueueAssets({ ids }))
      .then(() => {
        const routeParams = getState().routeParams;

        for (const assetId of ids) {
          /*
           * If deletion is happening from the search results page,
           * the search item will exist, but the asset might not.
           * If deletion is happening from asset page, the asset will exist,
           * but the search item might not.
           * Check both to get the asset type and to see if it's preloaded
           * content.
           */
          const asset =
            getState().entities.search_items[assetId] ||
            getState().entities.assets[assetId];
          const assetType = asset?.type;
          const preloaded = asset?.external_id === '__iconik_tutorial_content';
          dispatch(
            segmentTrackEvent({
              eventName: 'Asset Deleted',
              properties: {
                Type: assetType ?? 'UNKNOWN',
                'Preloaded Content': preloaded,
              },
            }),
          );
        }

        if (redirect === 'NONE') {
          return;
        }
        if (ids.length === 0) {
          return;
        }
        if (!routeParams.route.startsWith(ASSET_PAGE_ROUTE)) {
          return;
        }

        if (redirect === 'ROOT') {
          dispatch(historyPush('/'));
          return;
        }

        if (
          !routeParams.prevRoute ||
          routeParams.prevRoute.startsWith(ASSET_PAGE_ROUTE)
        ) {
          dispatch(historyReplace('/search'));
          return;
        }

        dispatch(historyGoBack());
      })
      .then(() => {
        const entityItems = Object.fromEntries(
          ids.map((id) => [id, { status: 'DELETED' as const }]),
        );
        const segmentUpdate = getSegmentUpdateAfterDelete(ids, getState());
        dispatch(
          updateEntities({
            search_items: entityItems,
            assets: entityItems,
            segments: segmentUpdate,
          }),
        );
      });
}

export function putAsset(
  id: string,
  data: AssetsAssetSchemaTypedPartial,
  clearNil = true,
) {
  return (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<AssetsAssetSchemaTypedWithId> => {
    const currentAsset = getState().entities.assets[id];
    const assetData = { ...currentAsset, ...data };
    const filteredData = clearNil ? _omitBy(assetData, _isNil) : assetData;
    return dispatch(putAssetRequest(id, filteredData)).then((response) => {
      const asset = asTypedAsset(response.body);
      dispatch(setAsset(asset));
      return asset;
    });
  };
}

const denormalizeAsset = createEntityDenormalizer('assets');

export function patchAsset(id: string, newData: AssetsAssetSchemaTypedPartial) {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const assetInStore = denormalizeAsset(getState().entities, id);
    if (!assetInStore) {
      return;
    }
    const filteredData = getChangedFields(assetInStore, newData);
    const response = await dispatch(httpPatchAssetsByAssetId(id, filteredData));
    const asset = asTypedAsset(response.body);
    dispatch(setAsset(asset));
    return asset;
  };
}

export function requestOriginal(formatId: string, storageId?: string) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    let copyStorageId = storageId;
    const filesStorageMatched =
      getState().storageMatchingReducer.filesStorageMatched;
    if (!copyStorageId && filesStorageMatched) {
      copyStorageId = filesStorageMatched.id;
    }

    if (!copyStorageId) {
      dispatch(errorMessage({ text: "Can't read destination storage" }));
      return Promise.resolve();
    }

    return dispatch(
      httpPostFormatsByFormatIdStoragesByStorageId(formatId, copyStorageId, {}),
    ).then(() => {
      dispatch(successMessage({ text: 'Original requested' }));
    });
  };
}

export const reindexAsset =
  (id: string) =>
  (dispatch: AppDispatch): Promise<void> => {
    const data = { sync_to_another_dc: true };
    return Promise.all([
      dispatch(httpPostAssetsByAssetIdReindex(id, data)),
      dispatch(httpPostAssetsByAssetIdSegmentsReindex(id, data)),
    ]).then(() => {
      dispatch(successMessage({ text: 'Asset will be reindexed' }));
    });
  };

export const deleteBulkRequest =
  (objectType: AssetsBulkDeleteSchema['object_type'], ids: string[]) =>
  (dispatch: AppDispatch) =>
    dispatch(
      httpPostDeleteQueueBulk({
        content_only: true,
        object_ids: ids,
        object_type: objectType,
      }),
    ).then(() => {
      dispatch(
        segmentTrackEvent({
          eventName: 'Asset Deleted',
          properties: { Type: 'BULK' },
        }),
      );
    });

export const approveRejectBulkRequest =
  (
    ids: string[],
    object_type: AssetsBulkSetApprovalSchema['object_type'],
    status: AssetsBulkSetApprovalSchema['status'],
  ) =>
  (dispatch: AppDispatch) =>
    dispatch(
      httpPostApprovalsBulk({
        object_ids: ids,
        object_type,
        status,
      }),
    );

export const removeApprovalsBulkRequest =
  (ids: string[], object_type: AssetsBulkSetApprovalSchema['object_type']) =>
  (dispatch: AppDispatch) =>
    dispatch(httpPostApprovalsBulkRemove({ object_ids: ids, object_type }));
